import type { SectionConfig, PageContent } from "@/contexts/ThemeContext";

export type BusinessType = "broker" | "builder" | "eco" | "luxury" | "urban";

export interface BusinessPageDef {
  id: string;
  label: string;
}

export interface BusinessConfig {
  type: BusinessType;
  label: string;
  description: string;
  pages: BusinessPageDef[];
  defaultSections: SectionConfig[];
  defaultPageContent: { [pageId: string]: PageContent };
}

const BUSINESS_CONFIGS: Record<BusinessType, BusinessConfig> = {
  broker: {
    type: "broker",
    label: "Real Estate Broker",
    description: "Property listings, consultations & market analysis",
    pages: [
      { id: "home", label: "Home" },
      { id: "properties", label: "Properties" },
      { id: "about", label: "About" },
      { id: "blog", label: "Blog" },
      { id: "contact", label: "Contact" },
    ],
    defaultSections: [
      { id: "hero", name: "Hero Banner", visible: true, order: 0 },
      { id: "partners", name: "Developer Partners", visible: true, order: 1 },
      { id: "featured-listings", name: "Featured Listings", visible: true, order: 2 },
      { id: "why-choose-us", name: "Why Choose Us", visible: true, order: 3 },
      { id: "testimonials", name: "Testimonials", visible: true, order: 4 },
      { id: "cta", name: "Call to Action", visible: true, order: 5 },
    ],
    defaultPageContent: {
      home: {
        hero: {
          subheading: "Gurgaon's Trusted Property Partner",
          heading: "Swipe Into Your Dream Home.",
          description: "We help you find premium residential & commercial properties in Golf Course Road, DLF City, Sohna Road, and other prime locations.",
          buttonText: "Get Free Consultation",
          buttonLink: "/contact",
          buttonText2: "Explore Properties",
          buttonLink2: "/properties",
          videoUrl: "https://videos.pexels.com/video-files/5490898/5490898-uhd_2560_1440_25fps.mp4",
          imageUrl: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80",
          stats: [
            { value: "8+", label: "Years Experience" },
            { value: "350+", label: "Properties Sold" },
            { value: "800+", label: "Happy Families" },
          ],
        },
        partners: {
          heading: "Trusted Partners",
          subheading: "Our Developer Network",
          description: "We work with India's most reputed developers.",
        },
        "featured-listings": {
          subheading: "Exclusive Listings",
          heading: "Featured Properties",
          description: "Handpicked premium properties in prime locations.",
        },
        "why-choose-us": {
          subheading: "Why Choose Us",
          heading: "The Advantage",
          description: "We go beyond traditional real estate services.",
          items: [
            { title: "RERA Verified", description: "All properties are legally verified for complete peace of mind." },
            { title: "Premium Properties", description: "Curated portfolio of luxury properties in prime locations." },
            { title: "Local Expertise", description: "Deep market knowledge with dedicated relationship managers." },
            { title: "Best Returns", description: "Properties with proven appreciation in high-growth corridors." },
            { title: "End-to-End Service", description: "From property tours to registration — we handle everything." },
            { title: "Exclusive Deals", description: "Access pre-launch offers and off-market properties." },
          ],
        },
        testimonials: {
          heading: "What Our Clients Say",
          subheading: "Testimonials",
          description: "Hear from our satisfied clients.",
        },
        cta: {
          heading: "Ready to Find Your Dream Property?",
          description: "Schedule a free consultation with our property experts.",
          buttonText: "Schedule Consultation",
          buttonLink: "/contact",
          buttonText2: "Call Now",
        },
      },
    },
  },

  builder: {
    type: "builder",
    label: "Builder / Developer",
    description: "Showcase projects, floor plans & construction progress",
    pages: [
      { id: "home", label: "Home" },
      { id: "projects", label: "Projects" },
      { id: "floorplans", label: "Floor Plans" },
      { id: "about", label: "About" },
      { id: "blog", label: "Blog" },
      { id: "contact", label: "Contact" },
    ],
    defaultSections: [
      { id: "hero", name: "Hero Banner", visible: true, order: 0 },
      { id: "ongoing-projects", name: "Ongoing Projects", visible: true, order: 1 },
      { id: "builder-amenities", name: "Amenities", visible: true, order: 2 },
      { id: "construction-progress", name: "Construction Progress", visible: true, order: 3 },
      { id: "testimonials", name: "Testimonials", visible: true, order: 4 },
      { id: "cta", name: "Call to Action", visible: true, order: 5 },
    ],
    defaultPageContent: {
      home: {
        hero: {
          subheading: "Building Tomorrow's Landmarks",
          heading: "Crafting Iconic Spaces.",
          description: "From vision to reality — delivering world-class residential and commercial developments.",
          buttonText: "Explore Projects",
          buttonLink: "/projects",
          buttonText2: "Download Brochure",
          buttonLink2: "/contact",
          imageUrl: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1920&q=80",
          stats: [
            { value: "25+", label: "Projects Delivered" },
            { value: "10M+", label: "Sq.Ft. Developed" },
            { value: "15K+", label: "Happy Homeowners" },
          ],
        },
        "ongoing-projects": {
          heading: "Ongoing Projects",
          subheading: "Our Developments",
          description: "Explore our latest developments.",
          items: [
            { title: "Skyline Residences", description: "Luxury Apartments · 320 Units · 75% Complete · Dec 2025", imageUrl: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&q=80" },
            { title: "Green Valley Township", description: "Integrated Township · 1200 Plots · 45% Complete · Mar 2026", imageUrl: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600&q=80" },
            { title: "Metro Business Hub", description: "Commercial Complex · 150 Offices · 90% Complete · Jun 2025", imageUrl: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&q=80" },
          ],
        },
        "builder-amenities": {
          heading: "World-Class Amenities",
          subheading: "Amenities",
          description: "Every project designed for modern living.",
          items: [
            { title: "Clubhouse", description: "30,000 sq.ft. world-class clubhouse" },
            { title: "Landscaped Gardens", description: "Acres of manicured green spaces" },
            { title: "24/7 Security", description: "Gated community with CCTV surveillance" },
            { title: "Sports Complex", description: "Tennis, basketball, swimming pool" },
            { title: "Premium Finishes", description: "Italian marble and imported fittings" },
            { title: "RERA Approved", description: "All projects fully compliant" },
          ],
        },
        "construction-progress": {
          heading: "Construction Updates",
          subheading: "Progress",
          description: "Track real-time progress on our projects.",
          items: [
            { title: "Phase 1 — Completed", description: "Foundation & structure work" },
            { title: "Phase 2 — In Progress", description: "Interior finishing & MEP" },
            { title: "Phase 3 — Upcoming", description: "Landscaping & amenities" },
            { title: "Phase 4 — Planned", description: "Handover & possession" },
          ],
        },
        testimonials: {
          heading: "What Homeowners Say",
          subheading: "Testimonials",
          description: "Hear from satisfied homeowners.",
        },
        cta: {
          heading: "Invest in Your Future Today",
          description: "Book a site visit or download our project brochure.",
          buttonText: "Book Site Visit",
          buttonLink: "/contact",
          buttonText2: "Call Now",
        },
      },
    },
  },

  eco: {
    type: "eco",
    label: "Eco-Friendly Builder / Township",
    description: "Sustainable living, green features & township amenities",
    pages: [
      { id: "home", label: "Home" },
      { id: "township", label: "Township" },
      { id: "sustainability", label: "Sustainability" },
      { id: "about", label: "About" },
      { id: "blog", label: "Blog" },
      { id: "contact", label: "Contact" },
    ],
    defaultSections: [
      { id: "hero", name: "Hero Banner", visible: true, order: 0 },
      { id: "green-features", name: "Green Features", visible: true, order: 1 },
      { id: "community-living", name: "Community Living", visible: true, order: 2 },
      { id: "sustainability-stats", name: "Sustainability Impact", visible: true, order: 3 },
      { id: "testimonials", name: "Testimonials", visible: true, order: 4 },
      { id: "cta", name: "Call to Action", visible: true, order: 5 },
    ],
    defaultPageContent: {
      home: {
        hero: {
          subheading: "Live in Harmony with Nature",
          heading: "Where Green Meets Grand.",
          description: "India's most sustainable township — solar-powered homes, organic gardens, and nature trails at your doorstep.",
          buttonText: "Explore Township",
          buttonLink: "/township",
          imageUrl: "https://images.unsplash.com/photo-1518005020951-eccb494ad742?w=1920&q=80",
          stats: [
            { value: "80%", label: "Green Cover" },
            { value: "Zero", label: "Carbon Footprint" },
            { value: "500+", label: "Trees Planted" },
          ],
        },
        "green-features": {
          heading: "Sustainable by Design",
          subheading: "Green Features",
          description: "Every element crafted for minimal environmental impact.",
          items: [
            { title: "Solar Powered", description: "100% solar energy for common areas and street lighting." },
            { title: "Rainwater Harvesting", description: "Advanced water recycling and conservation systems." },
            { title: "Waste Management", description: "Zero-waste community with composting and recycling." },
            { title: "Natural Ventilation", description: "Cross-ventilated designs reducing AC dependency by 40%." },
            { title: "Native Gardens", description: "Indigenous flora supporting local biodiversity." },
            { title: "Organic Farming", description: "Community organic garden for fresh, pesticide-free produce." },
          ],
        },
        "community-living": {
          heading: "Thriving Community",
          subheading: "Community Living",
          description: "A township designed for meaningful connections.",
          items: [
            { title: "Community Events", description: "Regular farmer's markets, yoga sessions, and cultural events.", imageUrl: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=500&q=80" },
            { title: "Car-Free Zones", description: "Dedicated cycling and walking paths throughout the township.", imageUrl: "https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=500&q=80" },
            { title: "Wellness Center", description: "Yoga studio, spa, and naturopathy center for holistic living.", imageUrl: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=500&q=80" },
          ],
        },
        "sustainability-stats": {
          heading: "Our Green Impact",
          subheading: "Impact",
          description: "Measurable sustainability milestones.",
          stats: [
            { value: "2.5M", label: "Liters Water Saved Annually" },
            { value: "1200", label: "Tons CO₂ Reduced" },
            { value: "95%", label: "Waste Recycled" },
            { value: "10K+", label: "Trees in Township" },
          ],
        },
        testimonials: {
          heading: "Resident Stories",
          subheading: "Testimonials",
          description: "What our residents say.",
        },
        cta: {
          heading: "Join the Green Revolution",
          description: "Experience eco-luxury living. Book a visit today.",
          buttonText: "Schedule Visit",
          buttonLink: "/contact",
          buttonText2: "Call Now",
        },
      },
    },
  },

  luxury: {
    type: "luxury",
    label: "Ultra-Luxury Broker / Penthouse",
    description: "Exclusive properties, concierge services & private viewings",
    pages: [
      { id: "home", label: "Home" },
      { id: "portfolio", label: "Portfolio" },
      { id: "lifestyle", label: "Lifestyle" },
      { id: "about", label: "About" },
      { id: "blog", label: "Blog" },
      { id: "contact", label: "Contact" },
    ],
    defaultSections: [
      { id: "hero", name: "Hero", visible: true, order: 0 },
      { id: "exclusive-gallery", name: "Exclusive Collection", visible: true, order: 1 },
      { id: "concierge-services", name: "Concierge Services", visible: true, order: 2 },
      { id: "luxury-lifestyle", name: "The Lifestyle", visible: true, order: 3 },
      { id: "testimonials", name: "Testimonials", visible: true, order: 4 },
      { id: "cta", name: "Private Inquiry", visible: true, order: 5 },
    ],
    defaultPageContent: {
      home: {
        hero: {
          subheading: "By Invitation Only",
          heading: "Residences Beyond Compare.",
          description: "Curating the world's most exclusive penthouses and ultra-luxury estates for discerning collectors.",
          buttonText: "Request Private Viewing",
          buttonLink: "/portfolio",
          buttonText2: "Private Inquiry",
          buttonLink2: "/contact",
          videoUrl: "https://videos.pexels.com/video-files/3773486/3773486-uhd_2560_1440_30fps.mp4",
          imageUrl: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=80",
          stats: [
            { value: "$2B+", label: "Portfolio Value" },
            { value: "50+", label: "Exclusive Estates" },
            { value: "100%", label: "Privacy Guaranteed" },
          ],
        },
        "exclusive-gallery": {
          heading: "The Collection",
          subheading: "Curated Selection",
          description: "Hand-selected properties for the world's elite.",
          items: [
            { title: "The Penthouse", description: "Golf Course Road · On Request", imageUrl: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80" },
            { title: "Villa Royale", description: "DLF Phase 5 · On Request", imageUrl: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80" },
            { title: "Sky Mansion", description: "Sector 54 · On Request", imageUrl: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80" },
            { title: "The Estate", description: "Chattarpur · On Request", imageUrl: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80" },
          ],
        },
        "concierge-services": {
          heading: "White Glove Service",
          subheading: "Bespoke Service",
          description: "End-to-end luxury real estate concierge.",
          imageUrl: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=700&q=80",
          items: [
            { title: "Private Viewings", description: "Exclusive property tours at your convenience." },
            { title: "Acquisition Advisory", description: "End-to-end support from search to possession." },
            { title: "Legal & Compliance", description: "Complete due diligence and documentation." },
            { title: "Interior Curation", description: "World-renowned interior design partnerships." },
          ],
        },
        "luxury-lifestyle": {
          heading: "The Art of Living",
          subheading: "Beyond Real Estate",
          description: "Where architecture meets aspiration.",
          items: [
            { title: "Private Art Collections", description: "Curated art experiences", imageUrl: "https://images.unsplash.com/photo-1577083288073-40892c0860a4?w=500&q=80" },
            { title: "Michelin Dining", description: "World-class culinary", imageUrl: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=500&q=80" },
            { title: "Yacht & Aviation", description: "Exclusive travel", imageUrl: "https://images.unsplash.com/photo-1540946485063-a40da27545f8?w=500&q=80" },
          ],
        },
        testimonials: {
          heading: "Client Testimonials",
          subheading: "Testimonials",
          description: "Trusted by the world's most discerning clients.",
        },
        cta: {
          heading: "Begin Your Private Journey",
          description: "Discreet inquiries welcome. Our advisors are available 24/7.",
          buttonText: "Schedule Private Consultation",
          buttonLink: "/contact",
          buttonText2: "Call Now",
        },
      },
    },
  },

  urban: {
    type: "urban",
    label: "City Apartments / Co-Living",
    description: "Urban spaces, community living & modern amenities",
    pages: [
      { id: "home", label: "Home" },
      { id: "spaces", label: "Spaces" },
      { id: "community", label: "Community" },
      { id: "about", label: "About" },
      { id: "blog", label: "Blog" },
      { id: "contact", label: "Contact" },
    ],
    defaultSections: [
      { id: "hero", name: "Hero Banner", visible: true, order: 0 },
      { id: "available-spaces", name: "Available Spaces", visible: true, order: 1 },
      { id: "amenities-grid", name: "Amenities", visible: true, order: 2 },
      { id: "community-events", name: "Community & Events", visible: true, order: 3 },
      { id: "pricing", name: "Pricing Plans", visible: true, order: 4 },
      { id: "cta", name: "Call to Action", visible: true, order: 5 },
    ],
    defaultPageContent: {
      home: {
        hero: {
          subheading: "Live. Work. Thrive.",
          heading: "Urban Living, Redefined.",
          description: "Fully-furnished apartments and co-living spaces in the heart of the city. Flexible leases, vibrant community.",
          buttonText: "Browse Spaces",
          buttonLink: "/spaces",
          buttonText2: "Our Community",
          buttonLink2: "/community",
          imageUrl: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1920&q=80",
          stats: [
            { value: "200+", label: "Rooms Available" },
            { value: "12", label: "Locations" },
            { value: "4.8★", label: "Resident Rating" },
          ],
        },
        "available-spaces": {
          heading: "Find Your Space",
          subheading: "Spaces",
          description: "From private studios to shared living — pick what suits you.",
          items: [
            { title: "Private Studio", description: "₹18,000/mo · 250 sq.ft · Private bathroom, Kitchenette, Balcony", imageUrl: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=500&q=80" },
            { title: "Shared Room", description: "₹8,000/mo · 150 sq.ft · 2-sharing, Common bath, All utilities", imageUrl: "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?w=500&q=80" },
            { title: "1BHK Apartment", description: "₹25,000/mo · 450 sq.ft · Full kitchen, Living room, Smart lock", imageUrl: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=500&q=80" },
          ],
        },
        "amenities-grid": {
          heading: "Everything You Need",
          subheading: "Amenities",
          description: "Curated amenities for modern city living.",
          items: [
            { title: "High-Speed WiFi", description: "Lightning-fast internet throughout" },
            { title: "Café & Lounge", description: "On-site café and social lounge" },
            { title: "Fitness Center", description: "Fully equipped gym" },
            { title: "24/7 Security", description: "Round-the-clock security" },
            { title: "Prime Locations", description: "Central city locations" },
            { title: "Co-working Space", description: "Dedicated work areas" },
            { title: "Game Room", description: "Entertainment and recreation" },
            { title: "Rooftop Terrace", description: "Panoramic city views" },
          ],
        },
        "community-events": {
          heading: "Never a Dull Moment",
          subheading: "Events",
          description: "Weekly events, networking nights, and more.",
          items: [
            { title: "Rooftop Movie Night", description: "FRI 21 · 7:30 PM · Social" },
            { title: "Startup Pitch Day", description: "SAT 22 · 2:00 PM · Networking" },
            { title: "Sunday Brunch", description: "SUN 23 · 11:00 AM · Food" },
            { title: "Yoga & Meditation", description: "WED 26 · 6:30 AM · Wellness" },
          ],
        },
        pricing: {
          heading: "Simple, Transparent Pricing",
          subheading: "Plans",
          description: "All-inclusive plans. No hidden charges.",
          items: [
            { title: "Shared — ₹8,000/mo", description: "2-sharing room, Common areas, Basic WiFi, Housekeeping" },
            { title: "Private Studio — ₹18,000/mo", description: "Private room, Attached bath, High-speed WiFi, All utilities, Events access" },
            { title: "1BHK Premium — ₹25,000/mo", description: "Full apartment, Kitchen, Smart lock, Premium WiFi, Events access, Parking" },
          ],
        },
        cta: {
          heading: "Move In This Week",
          description: "No brokers. No hassle. Just great living.",
          buttonText: "Book a Tour",
          buttonLink: "/contact",
          buttonText2: "Call Now",
        },
      },
    },
  },
};

export function getBusinessConfig(type: BusinessType): BusinessConfig {
  return BUSINESS_CONFIGS[type];
}

export function getAllBusinessConfigs(): BusinessConfig[] {
  return Object.values(BUSINESS_CONFIGS);
}

export { BUSINESS_CONFIGS };
